import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs/operators';
import { ToastButtons, ToastMessageType, ToastService } from '../../../../../Common/toast';
import { FeedTypes } from '../../../../../Constants/FeedTypes';
import { TenantService } from '../../../../../Services';
import { FeedService } from '../../Services/feed.service';
import { IFeedConfiguration } from '../../Services/IFeedConfiguration';
import { IFeedConfigurationDataField } from '../../Services/IFeedConfigurationDataField';
import {
    ImportService,
    NewImportViewModel,
    RawRow
} from "../../Services/import.service";

import { MatCard, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { MatFormField, MatInput, MatHint } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'aw-hierarchy-import',
    templateUrl: './hierarchy-import.component.html',
    styleUrls: ['./hierarchy-import.component.less'],
    imports: [MatCard, MatCardTitle, MatCardSubtitle, MatCardContent, FormsModule, MatFormField, MatInput, MatHint, MatButton, MatIcon, RouterLink]
})
export class HierarchyImportComponent implements OnInit {
    @ViewChild('folderLocationForm') public folderLocationForm: NgForm;
    public hasExistingHierarchyFeed = false;
    public viewModel: NewImportViewModel;
    public model: IFeedConfiguration;
    private importType = "hierarchy";

    private lists = {};

    private msgUniqueFilePath = "Folder Location must be unique";
    private msgRequiredLocation = "Folder Location must be entered";
    private msgRequiredCSVFile = "No CSV file found in the specified folder";
    private msgErrorNoFile = "No file found in the specified folder";

    public fetchFileErrorText: string = null;
    public importValid = true;
    public busy = true;
    private selectedModuleId: string = null;
    private selectedStateId: string = null;
    private folderLocation: string;
    public tenantName: string;

    public existingFeedConfiguration: IFeedConfiguration;
    private existingFeeds: IFeedConfiguration[];

    constructor(
        private importService: ImportService,
        private feedService: FeedService,
        private tenantService: TenantService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private toast: ToastService) { }

    ngOnInit() {
        const tenant = this.tenantService.selectedTenant;

        this.feedService.getFeedConfigurations(tenant.Value).subscribe((feedConfigurations: IFeedConfiguration[]) => {
            this.existingFeedConfiguration = feedConfigurations.find(feed => feed.FeedType === FeedTypes[FeedTypes.HierarchyFeed] && feed.Enabled === true);
            this.hasExistingHierarchyFeed = !!this.existingFeedConfiguration;
            this.existingFeeds = feedConfigurations;
            this.busy = false;
        });
        this.model = this.feedService.getNewFeedConfiguration();
        this.importService.getNewViewModel(this.importType, tenant.TenantId)
            .subscribe(
                r => {
                    this.viewModel = r;
                }
            );

        this.tenantName = tenant.Value;
    }

    validate() {
        this.busy = true;
        this.importService.validate(this.viewModel.Id, this.selectedModuleId, this.selectedStateId)
            .subscribe(r => {
                this.viewModel = r;
                this.busy = false;
            }
            );
    }

    fetchFile() {
        this.importValid = true;

        if (!this.model.SftpLocation) {
            this.importValid = false;
            this.fetchFileErrorText = this.msgRequiredLocation;
            return;
        }

        this.feedService.getFeedConfigurationsFromPath(this.tenantService.selectedTenant.Value, this.model.SftpLocation, this.model.FilePattern)
            .subscribe(data => {
                const unique = data.filter(x => x.Id !== this.model.Id).length === 0;
                if (!unique) {
                    this.importValid = false;
                    this.fetchFileErrorText = this.msgUniqueFilePath;
                    return;
                }
                else {
                    this.fetchFileErrorText = null;
                    this.importService
                        .uploadFileFromLocation(this.model.SftpLocation, this.model.FilePattern, this.importType, { Id: 2, Extensions: 'csv', Name: 'csv' }, this.viewModel.Id, this.selectedModuleId).pipe(map(r => r)).subscribe(data => {
                            this.busy = false;
                            this.viewModel = data as NewImportViewModel;
                        },
                        () => {
                            this.importValid = false;
                            this.fetchFileErrorText = this.msgRequiredCSVFile;
                            this.busy = false;
                        });
                    this.importService.getLatestFileContentsFromFolder(this.tenantName, this.model.SftpLocation, this.model.FilePattern).subscribe(
                        fileContents => {
                            this.folderLocation = this.model.SftpLocation;
                            this.model.DataFields = this.createInitialFieldConfigurationDataFields(fileContents[0]);
                        },
                        () => {
                            this.importValid = false;
                            this.fetchFileErrorText = this.msgErrorNoFile;
                            this.busy = false;
                        });
                }
            });
    };

    createInitialFieldConfigurationDataFields(content): IFeedConfigurationDataField[] {
        let level = 1;
        const dataFields = Object.keys(content).map((value, index) => {
            const isName = Math.abs(index % 2) === 1;
            const feedConfigurationData: IFeedConfigurationDataField = {
                Name: value,
                Index: index,
                DataType: 'hierarchy',
                ListKey: !isName ? 'all_nodes_shortnames' : 'all_nodes',
                FormatString: null,
                IsSearchKey: false,
                IsNaturalKey: false,
                IsIdentifier: false,
                SyncList: false,
                LocationLevel: level > 6 ? 'REPORTING_POINT' : `LEVEL${level}`,
                DisableSync: false,
                RecordPropertyName: null,
                Enabled: true,
                Path: [],
                ModelPropertyName: null,
                LinkedRecordExternalIdProperty: null,
                LinkedRecordModuleId: null,
                ListType: null,
                AttributeType: null,
                RegexRestrict: null,
                MaxChars: null,
                MinChars: null,
                AttributeId: null,
                AttributeMappings: []
            };

            if (isName) {
                level += 1;
            }

            return feedConfigurationData;
        });
        return dataFields;
    }

    createNewFeedName(tenant) {
        let name = tenant + " Hierarchy Feed (auto)";
        let i = 1;
        while (this.existingFeeds.some(f => f.Name === name)) {
            name = tenant + " Hierarchy Feed (auto) " + i;
            i++;
        }
        return name;
    }

    createFeed() {
        const tenant = this.tenantService.selectedTenant.Value;
        const name = this.createNewFeedName(tenant);
        this.model.Name = name;
        this.model.Description = name;
        this.model.FeedType = FeedTypes[FeedTypes.HierarchyFeed];
        this.model.SftpLocation = this.folderLocation;
        this.model.FileNameRegex = this.tenantName + '_';
        this.model.Enabled = true;
        this.model.EmailOnSuccess = false;
        this.model.Threshold = 0;
        this.feedService.saveFeedConfiguration(this.tenantService.selectedTenant.Value, this.model)
            .subscribe(
                (result) => {
                    this.model.Id = result;
                    this.router.navigate(['../../../', 'feeds', this.model.Id],
                        {
                            queryParamsHandling: 'preserve',
                            relativeTo: this.activatedRoute
                        });
                    this.toast.sendMessage('Created Successfully', 'Your feed has been created successfully', ToastMessageType.success, ToastButtons.Ok, 0)
                });
    }

    getFieldClass(r: RawRow, i: number) {
        return r.Errors.filter(e => e.ColumnIndex === i).length > 0
            ? "error"
            : "";
    }

    hasErrors() {
        if (!this.viewModel) return false;
        return (this.viewModel.ErrorRows && this.viewModel.ErrorRows.length > 0) ||
            (this.viewModel.NodeErrors && this.viewModel.NodeErrors.length > 0) ||
            this.viewModel.Failed;
    }
}
